if (!customElements.get('cart-remove-button')) {
  class CartRemoveButton extends HTMLElement {
    constructor() {
      super();
      window.initScriptOnDemand(this, this.init.bind(this));
    }

    init() {
      this.addEventListener('click', (event) => {
        event.preventDefault();
        const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
        cartItems.updateQuantity(this.dataset.index, 0);
      });
    }
  }

  customElements.define('cart-remove-button', CartRemoveButton);
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    window.initScriptOnDemand(this, this.init.bind(this));
  }

  init() {
    this.cartDrawer = document.querySelector('cart-drawer');
    this.lineItemStatusElement = this.getCartScope().querySelector('#shopping-cart-line-item-status, #CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });

    // JDM 2026-10-25 (Upload 17): global.js publish() renames cartUpdate to cartAdd (product-form) and
    // cartRemove (cart-items without variantId), so the /cart page never heard adds made elsewhere.
    // Page instance only: the drawer (cart-drawer-items) keeps the stock behaviour, so it never double-renders.
    if (this.tagName !== 'CART-ITEMS') return;
    this.cartAddUnsubscriber = subscribe(PUB_SUB_EVENTS.cartAdd, (event) => {
      // product-form already rendered main-cart into this page when it asked for that section.
      if (typeof event?.cartData?.sections?.['main-cart'] === 'string') {
        this.cartSignature = undefined;
        return;
      }
      this.onCartUpdate();
    });
    this.cartRemoveUnsubscriber = subscribe(PUB_SUB_EVENTS.cartRemove, (event) => {
      if (event?.source === 'cart-items') return;
      this.onCartUpdate();
    });
    // Back/forward cache restore, or returning to a /cart tab after adding in another tab.
    this.onJdmPageShow = (event) => {
      if (event.persisted) this.refreshIfStale();
    };
    this.onJdmVisibility = () => {
      if (document.visibilityState === 'visible') this.refreshIfStale();
    };
    window.addEventListener('pageshow', this.onJdmPageShow);
    document.addEventListener('visibilitychange', this.onJdmVisibility);
    setTimeout(() => this.fetchCart().then((cart) => {
      if (this.cartSignature === undefined) this.cartSignature = this.getCartSignature(cart);
    }).catch(() => {}), 0);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.cartAddUnsubscriber) this.cartAddUnsubscriber();
    if (this.cartRemoveUnsubscriber) this.cartRemoveUnsubscriber();
    if (this.onJdmPageShow) window.removeEventListener('pageshow', this.onJdmPageShow);
    if (this.onJdmVisibility) document.removeEventListener('visibilitychange', this.onJdmVisibility);
  }

  fetchCart() {
    return fetch(`${routes.cart_url}.js`, { cache: 'no-store', headers: { Accept: 'application/json' } }).then((response) => response.json());
  }

  getCartSignature(cart) {
    if (!cart || !Array.isArray(cart.items)) return undefined;
    return [cart.item_count, cart.total_price, ...cart.items.map((item) => `${item.key}:${item.quantity}`)].join('|');
  }

  refreshIfStale() {
    if (this.jdmRefreshing) return;
    this.jdmRefreshing = true;
    this.fetchCart()
      .then((cart) => {
        const signature = this.getCartSignature(cart);
        if (signature === undefined || signature === this.cartSignature) return;
        return this.onCartUpdate().then(() => {
          // The drawer was rendered with the old cart too; cart-drawer.js reload() refreshes it without opening it.
          if (typeof this.cartDrawer?.reload === 'function') this.cartDrawer.reload();
        });
      })
      .catch((e) => console.error(e))
      .finally(() => {
        this.jdmRefreshing = false;
      });
  }

  // The drawer survives section rendering; its cart-drawer-items child does not.
  getCartScope() {
    return this.closest('cart-drawer') || (this.tagName === 'CART-DRAWER-ITEMS' ? this.cartDrawer : null) || this.closest('#main-cart') || this;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`) || this.querySelector(`#Drawer-quantity-${id}`);
    if (input) input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue > 0 && inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          this.cartDrawer?.removeDeleteTabIndex();
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      // JDM Upload 17: re-render items, subtotal, free-shipping bar and cart bubble from one sections request.
      const sectionsToRender = this.getSectionsToRender().filter((section) => document.getElementById(section.id));
      const sectionIds = [...new Set(sectionsToRender.map((section) => section.section))];
      return Promise.all([
        fetch(`${routes.cart_url}?sections=${sectionIds.join(',')}`, { cache: 'no-store' }).then((response) => response.json()),
        this.fetchCart(),
      ])
        .then(([sections, cart]) => {
          if (!sections || typeof sections['main-cart'] !== 'string') throw new Error('main-cart section missing');
          this.renderContents({ ...cart, sections }, sectionsToRender);
          this.cartSignature = this.getCartSignature(cart);
          window.renderSelects();
          window.loadDesktopOnlyTemplates();
          this.cartDrawer?.removeDeleteTabIndex();
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: 'main-cart',
        selector: '.js-cart-items',
      },
      {
        id: 'cart-summary',
        section: 'main-cart',
        selector: '.js-subtotal',
      },
      {
        id: 'cart-summary',
        section: 'main-cart',
        selector: '.js-free-shipping-bar',
      },
      {
        id: 'cart-summary',
        section: 'main-cart',
        selector: '.cart__recommendations',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      }
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    const cartScope = this.getCartScope();
    const owningDrawer = this.closest('cart-drawer');
    const canRestoreFocus = () => owningDrawer
      ? owningDrawer.isConnected && owningDrawer.classList.contains('active')
      : !(this.cartDrawer?.isConnected && this.cartDrawer.classList.contains('active'));
    this.enableLoading(line);

    let sectionsToRender = [];

    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems) {
      sectionsToRender = [...sectionsToRender, ...cartDrawerItems.getSectionsToRender()];
    }

    const cartItems = document.querySelector('cart-items');
    if (cartItems) {
      sectionsToRender = [...sectionsToRender, ...cartItems.getSectionsToRender()];
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: sectionsToRender.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
            cartScope.querySelector(`#Quantity-${line}`) || cartScope.querySelector(`#Drawer-quantity-${line}`);
        const items = cartScope.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          if (quantityElement && canRestoreFocus()) {
            owningDrawer ? trapFocus(owningDrawer, quantityElement) : quantityElement.focus();
          }
          return;
        }

        this.renderContents(parsedState, sectionsToRender);

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (quantityElement && items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);
        // Do not let a late response move focus into a drawer the customer closed.
        if (canRestoreFocus()) {
          const lineItem = cartScope.querySelector(`#CartItem-${line}`) || cartScope.querySelector(`#CartDrawer-Item-${line}`);
          let focusTarget = name && lineItem ? lineItem.querySelector(`[name="${name}"]`) : null;
          let focusContainer = owningDrawer;
          if (!focusTarget && parsedState.item_count === 0) {
            focusContainer = owningDrawer?.querySelector('.drawer__inner-empty') || owningDrawer;
            focusTarget = owningDrawer
              ? focusContainer?.querySelector('a, button')
              : cartScope.querySelector('.continue-shopping-empty');
          } else if (!focusTarget) {
            focusTarget = cartScope.querySelector('.cart-item__name');
          }
          if (focusTarget) {
            owningDrawer ? trapFocus(focusContainer, focusTarget) : focusTarget.focus();
          }
        }
        window.renderSelects();
        window.loadDesktopOnlyTemplates();

        const pageCartItems = document.querySelector('cart-items');
        if (pageCartItems && typeof pageCartItems.getCartSignature === 'function') {
          pageCartItems.cartSignature = pageCartItems.getCartSignature(parsedState);
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch((err) => {
        console.warn(err);
        this.querySelectorAll('.loading__spinner').forEach((spinner) => spinner.classList.add('hidden', 'spinning-complete'));
        const errors = cartScope.querySelector('#cart-errors, #CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        if (typeof this.cartDrawer?.removeDeleteTabIndex === 'function') this.cartDrawer?.removeDeleteTabIndex();
        this.disableLoading(line);
      });
  }

  renderContents(parsedState, sectionsToRender) {
    if (!sectionsToRender) sectionsToRender = this.getSectionsToRender();

    this.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cart = document.getElementById('main-cart');

    if (cart) cart.classList.toggle('is-empty', parsedState.item_count === 0);
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

    sectionsToRender.forEach((section) => {
      const elementToReplace =
          document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
      const newHtml = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
      );

      if (newHtml) {
        // Prevent a flash of content with recommendations
        const oldRecommendations = elementToReplace.querySelector('product-recommendations');
        if (oldRecommendations) {
          const newRecommendations = newHtml.querySelector('product-recommendations');
          if (newRecommendations) {
            newRecommendations.innerHTML = oldRecommendations.innerHTML;
          }
        }

        // Animate free shipping progress bar from old number to new number
        const oldFreeShippingBar = elementToReplace.querySelector('.free-shipping-bar');
        if (oldFreeShippingBar) {
          const oldProgress = oldFreeShippingBar.style.getPropertyValue('--shipping-bar-width');
          const newFreeShippingBar = newHtml.querySelector('.free-shipping-bar');
          const newProgress = newFreeShippingBar.style.getPropertyValue('--shipping-bar-width');
          newFreeShippingBar.style.setProperty('--shipping-bar-width', oldProgress);

          setTimeout(() => {
            elementToReplace.querySelector('.free-shipping-bar').style
              .setProperty('--shipping-bar-width', newProgress);
          }, 0);
        }

        elementToReplace.innerHTML = newHtml.innerHTML;
        window.loadTemplateContent(this);
      }
    });
  }

  updateLiveRegions(line, message) {
    const cartScope = this.getCartScope();
    const lineItemError =
      cartScope.querySelector(`#Line-item-error-${line}`) || cartScope.querySelector(`#CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    const lineItemStatus = cartScope.querySelector('#shopping-cart-line-item-status, #CartDrawer-LineItemStatus');
    lineItemStatus?.setAttribute('aria-hidden', true);

    const cartStatus = cartScope.querySelector('#cart-live-region-text, #CartDrawer-LiveRegionText');
    if (!cartStatus) return;
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    html = html.replace(/<template\b[^>]*>|<\/template>/g, '');
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  enableLoading(line) {
    const mainCartItems = this.getCartScope().querySelector('#main-cart-items, #CartDrawer-CartItems');
    mainCartItems?.classList.add('cart__items--disabled');

    const cartItemElements = this.getCartScope().querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.getCartScope().querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);

    this.closest('cart-drawer')?.classList.add('closable-element--no-trans');
  }

  disableLoading(line) {
    const mainCartItems = this.getCartScope().querySelector('#main-cart-items, #CartDrawer-CartItems');
    mainCartItems?.classList.remove('cart__items--disabled');

    const cartItemElements = this.getCartScope().querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.getCartScope().querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));

    const owningDrawer = this.tagName === 'CART-DRAWER-ITEMS' ? this.getCartScope() : null;
    window.requestAnimationFrame(() => {
      owningDrawer?.classList.remove('closable-element--no-trans');
    });
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        window.initScriptOnDemand(this, this.init.bind(this));
      }

      init() {
        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
